import { useState, useCallback, useEffect } from 'react';
import { getPatientList, getPatientById, clearPatientCache } from '@/api/patients';
import { isExpiredSessionError } from '@/api/types';
import type { Patient, PatientFilters } from '@/types/patient';
import { useToast } from '@/contexts/toastContextBase';

export function usePatients(initialFilters?: PatientFilters) {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<PatientFilters>(initialFilters || {});
  const { showToast } = useToast();

  const fetchPatients = useCallback(async (forceRefresh = false) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getPatientList(filters, forceRefresh);
      let filtered = response.patients;

      if (filters.search) {
        const search = filters.search.toLowerCase();
        filtered = filtered.filter(
          (p) =>
            p.firstName.toLowerCase().includes(search) ||
            p.lastName.toLowerCase().includes(search) ||
            p.username.toLowerCase().includes(search)
        );
      }

      if (filters.status) {
        filtered = filtered.filter((p) => p.status === filters.status);
      }

      setPatients(filtered);
    } catch (err) {
      if (isExpiredSessionError(err)) {
        return;
      }

      const message = err instanceof Error ? err.message : 'Failed to load patients';
      setError(new Error(message));
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const updateFilters = useCallback((newFilters: Partial<PatientFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  const refetch = useCallback(() => fetchPatients(true), [fetchPatients]);

  return {
    patients,
    isLoading,
    error,
    filters,
    updateFilters,
    clearFilters,
    refetch,
  };
}

// Export cache clear for use on logout
export { clearPatientCache };

export function usePatient(patientId: string | undefined) {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (!patientId) {
      setPatient(null);
      setIsLoading(false);
      return;
    }

    const fetchPatient = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await getPatientById(patientId);
        if (!data) {
          throw new Error('Patient not found');
        }
        setPatient(data);
      } catch (err) {
        if (isExpiredSessionError(err)) {
          return;
        }

        const message = err instanceof Error ? err.message : 'Failed to load patient';
        setError(new Error(message));
        showToast('error', message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatient();
  }, [patientId, showToast]);

  return { patient, isLoading, error };
}
